import dayjs from 'dayjs'
import { FormikProps } from 'formik'
import { ILoggedInUser } from '../features/types'

/**
 * Date formatting utilities - ISO 8601 format (YYYY-MM-DD)
 * Use these functions throughout the app for consistent date display
 */

/**
 * Format a date to ISO 8601 date format (YYYY-MM-DD)
 * Returns '-' for null/undefined values
 * Note: This formats the date as-is without timezone conversion
 */
export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return '-'
  return dayjs(date).format('YYYY-MM-DD')
}

/**
 * Format a UTC date to local timezone in ISO 8601 date format (YYYY-MM-DD)
 * Use this when the source date is in UTC and needs to be displayed in the user's local timezone
 * Returns '-' for null/undefined values
 */
export const formatLocalDate = (date: string | Date | null | undefined): string => {
  if (!date) return '-'
  return new Date(date).toLocaleDateString('en-CA') // en-CA gives YYYY-MM-DD format
}

/**
 * Format a date to ISO 8601 datetime format (YYYY-MM-DD HH:mm)
 * Returns '-' for null/undefined values
 */
export const formatDateTime = (date: string | Date | null | undefined): string => {
  if (!date) return '-'
  return dayjs(date).format('YYYY-MM-DD HH:mm')
}

/**
 * Format a date to ISO 8601 datetime with seconds (YYYY-MM-DD HH:mm:ss)
 * Returns '-' for null/undefined values
 */
export const formatDateTimeWithSeconds = (date: string | Date | null | undefined): string => {
  if (!date) return '-'
  return dayjs(date).format('YYYY-MM-DD HH:mm:ss')
}

/**
 * Format a date for datetime-local input (YYYY-MM-DDTHH:mm)
 * Returns empty string for null/undefined values
 */
export const formatDateTimeForInput = (date: string | Date | null | undefined): string => {
  if (!date) return ''
  return dayjs(date).format('YYYY-MM-DDTHH:mm')
}

/**
 * Format a date for API requests (YYYY-MM-DDTHH:mm:ss)
 * Returns empty string for null/undefined values
 */
export const formatDateTimeForApi = (date: string | Date | null | undefined): string => {
  if (!date) return ''
  return dayjs(date).format('YYYY-MM-DDTHH:mm:ss')
}

export const getProperty = <T>(o: T, propertyName: string): T[keyof T] => {
  return o[propertyName as keyof T]
}

const get = (from: any, ...selectors: any) => {
  return [...selectors].map((s) =>
    s
      .replace(/\[([^[\]]*)\]/g, '.$1.')
      .split('.')
      .filter((t: any) => t !== '')
      .reduce((prev: any, cur: any) => prev && prev[cur], from)
  )
}

export const getError = (formik: FormikProps<any>, id: string) => {
  const touched = formik.touched && get(formik.touched, id)[0]
  const errorMessage = formik.errors && get(formik.errors, id)[0]
  return touched && errorMessage
}

export const REQUEST_INITIAL_STATE = {
  data: null,
  error: null,
  isFetching: false,
  isFinsihed: false,
}

export type ParsedAddress = {
  address1: string
  address2?: string
  city: string
  state: string
  postalCode: string
  country: any
  premise?: string
}

interface IStringMap {
  [key: string]: string[]
}

const ShouldBeComponent: IStringMap = {
  // address1: ['street_number', 'route'],
  address2: ['sublocality_level_1', 'sublocality', 'neighborhood'],
  city: ['locality', 'postal_town', 'sublocality_level_1'],
  state: ['administrative_area_level_1'],
  postalCode: ['postal_code'],
  country: ['country'],
  premise: ['premise', 'subpremise'],
}

export const parseGeoCoderResult = (geoCoderResult: any): ParsedAddress => {
  const addressComponents = geoCoderResult
  const parsedAddress: ParsedAddress = {
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  }

  addressComponents.forEach((component: any) => {
    for (const [key, types] of Object.entries(ShouldBeComponent)) {
      if (types.some((type) => component.types.includes(type))) {
        if (key === 'country') {
          parsedAddress.country = component
        } else {
          ;(parsedAddress as any)[key] = component.long_name
        }
        break
      }
    }
  })
  return parsedAddress
}

export const getCompanyName = (user: ILoggedInUser | null): string | null | undefined => {
  if (!user) return null

  if (user.customerId) {
    const selectedCustomer = user.customers.find((customer) => customer.id === user.customerId)
    return selectedCustomer ? selectedCustomer.name : null
  }

  if (user.tenantId) {
    const selectedTenant = user.tenants.find((tenant) => tenant.id === user.tenantId)
    return selectedTenant ? selectedTenant.name ?? '' : null
  }

  return null
}

export const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  }
}

export const isEmptyObject = (obj: object): boolean => {
  return Object.entries(obj).length === 0
}

export const getLastDayOfCurrentYear = (): Date => {
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear()
  return new Date(currentYear, 11, 31)
}

interface LabelValue {
  label: string
  value: string | number
}

export const convertToLabelValue = <T extends Record<string, any>>(
  items: T[],
  labelKeys: (keyof T)[] = ['name'],
  valueKey: keyof T = 'id'
): LabelValue[] => {
  return items.map((item) => ({
    label: labelKeys.map((key) => String(item[key])).join(' '),
    value: item[valueKey],
  }))
}

export const formatValue = (
  val: string | number,
  options?: {
    minimumFractionDigits?: number
    maximumFractionDigits?: number
    locale?: string
  }
): string => {
  const defaultOptions = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    locale: undefined,
  }

  const formatOptions = { ...defaultOptions, ...options }

  if (typeof val === 'number') {
    return val.toLocaleString(formatOptions.locale, {
      minimumFractionDigits: formatOptions.minimumFractionDigits,
      maximumFractionDigits: formatOptions.maximumFractionDigits,
    })
  }

  const numValue = parseFloat(val)
  if (!isNaN(numValue) && isFinite(numValue)) {
    return numValue.toLocaleString(formatOptions.locale, {
      minimumFractionDigits: formatOptions.minimumFractionDigits,
      maximumFractionDigits: formatOptions.maximumFractionDigits,
    })
  }

  return val
}

export const getErrorByPath = (obj: any, path: string) => {
  if (!path) return undefined
  return path.split('.').reduce((acc, part) => (acc && acc[part] ? acc[part] : undefined), obj)
}
